import { gql, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  MenuItem
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import {
  useAdmin,
  useLoginInfoRefetch,
  useTenant
} from "src/components/LoggedInAdminProvider";
import { useSubaccount } from "src/components/SubaccountProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import SubmitButton from "src/components/form/SubmitButton";
import { JOB_FIELDS } from "src/graphql/fragments";
import { configureMessaging as configureMessagingType } from "src/graphql/generated/configureMessaging";
import { getActiveMessagingConfigurationJob } from "src/graphql/generated/getActiveMessagingConfigurationJob";
import {
  JobStatus,
  MessagingPhoneNumberStatus
} from "src/graphql/generated/globalTypes";
import { getErrorMessage } from "src/util/errors";
import { useFeatureFlag } from "src/util/featureFlags";
import formatPhoneNumber from "src/util/formatPhoneNumber";
import { messagingNumberIsActive } from "src/util/messaging";
import useMutation from "src/util/useMutation";
import yup from "src/util/yup";

const GET_ACTIVE_JOB = gql`
  ${JOB_FIELDS}
  query getActiveMessagingConfigurationJob($subaccountId: ID!) {
    listJobs(
      filter: { type: CONFIGURE_MESSAGING_NUMBER }
      sort: { sortBy: CREATED_AT, direction: DESC }
      subaccountId: $subaccountId
    ) {
      totalCount
      jobs {
        ...JobFields
      }
    }
  }
`;

const CONFIGURE_MESSAGING = gql`
  ${JOB_FIELDS}
  mutation configureMessaging($subaccountId: ID!) {
    subaccountConfigureMessagingNumber(subaccountId: $subaccountId) {
      ...JobFields
    }
  }
`;

const EDIT_MESSAGING_NUMBER = gql`
  mutation subaccountEditMessagingNumber(
    $subaccountId: ID!
    $messagingPhoneNumber: String!
    $messagingPhoneNumberStatus: MessagingPhoneNumberStatus!
  ) {
    subaccountEditMessagingNumber(
      subaccountId: $subaccountId
      messagingPhoneNumber: $messagingPhoneNumber
      messagingPhoneNumberStatus: $messagingPhoneNumberStatus
    ) {
      messagingPhoneNumber
      messagingPhoneNumberStatus
    }
  }
`;

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  margin: {
    marginLeft: theme.spacing(1)
  }
}));

export default function Messaging(): JSX.Element {
  const admin = useAdmin()!;
  if (admin.isStaff) {
    return <StaffMessaging />;
  } else {
    return <AdminMessaging />;
  }
}

function AdminMessaging(): JSX.Element {
  const tenant = useTenant()!;
  const { subaccount } = useSubaccount()!;
  const subaccountTerm = tenant.isUsingSubaccounts
    ? "subaccount"
    : "organization";

  return (
    <Card>
      <CardHeader title="Messaging" />
      <CardContent>
        {subaccount.messagingPhoneNumber &&
        messagingNumberIsActive(
          tenant.featureFlags.preventMessagingFromUnregisteredNumbers,
          subaccount.messagingPhoneNumberStatus
        ) ? (
          <ActiveMessagingNumberMessage
            number={subaccount.messagingPhoneNumber}
          />
        ) : (
          <Alert severity="warning">
            Text messaging is not yet enabled for this {subaccountTerm}.{" "}
            <a
              href={`https://scaletowin.jotform.com/250076899887076?tenant=${tenant.slug}&subaccount_id=${subaccount.id}`}
              target="_blank"
              rel="noopener noreferer"
            >
              Please use this form to request a phone number for text messaging
            </a>
            .
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function StaffMessaging(): JSX.Element {
  const { subaccount } = useSubaccount()!;
  const checkStatusFlag = useFeatureFlag(
    "preventMessagingFromUnregisteredNumbers"
  );
  const refetchLoginInfo = useLoginInfoRefetch();
  const [refetchingTenant, setRefetchingTenant] = useState(false);
  const refetchTenant = useCallback(async () => {
    setRefetchingTenant(true);
    try {
      await refetchLoginInfo();
    } finally {
      setRefetchingTenant(false);
    }
  }, [setRefetchingTenant, refetchLoginInfo]);

  const {
    data: jobData,
    loading: jobDataLoading,
    startPolling: startActiveJobPolling,
    stopPolling: stopActiveJobPolling,
    previousData: previousJobData
  } = useQuery<getActiveMessagingConfigurationJob>(GET_ACTIVE_JOB, {
    variables: { subaccountId: subaccount.id }
  });
  const completedStatuses = [JobStatus.DONE, JobStatus.FAILED];
  const mostRecentJob = _.first(jobData?.listJobs.jobs);
  const configurationIsRunning =
    mostRecentJob && !completedStatuses.includes(mostRecentJob.status);
  const previousJob = _.first(previousJobData?.listJobs.jobs);
  const configurationWasRunning =
    previousJob && !completedStatuses.includes(previousJob.status);

  useEffect(() => {
    if (configurationIsRunning) {
      startActiveJobPolling(500);
    } else if (configurationWasRunning) {
      stopActiveJobPolling();
      void refetchTenant();
    }
  }, [
    configurationIsRunning,
    configurationWasRunning,
    startActiveJobPolling,
    stopActiveJobPolling,
    refetchTenant
  ]);

  const [configureMessaging] = useMutation<configureMessagingType>(
    CONFIGURE_MESSAGING,
    {
      refetchQueries: ["getActiveMessagingConfigurationJob", "loggedInInfo"],
      awaitRefetchQueries: true,
      variables: {
        subaccountId: subaccount.id
      }
    }
  );

  const [startingConfiguration, setStartingConfiguration] = useState(false);
  const [startConfigurationError, setStartConfigurationError] = useState<
    string | null
  >(null);

  const handleConfigureMessaging = useCallback(async () => {
    setStartingConfiguration(true);
    setStartConfigurationError(null);

    const { errors } = await configureMessaging();

    setStartingConfiguration(false);
    if (errors) {
      setStartConfigurationError(getErrorMessage(errors));
    }
  }, [
    setStartingConfiguration,
    setStartConfigurationError,
    configureMessaging
  ]);

  const loading =
    jobDataLoading ||
    refetchingTenant ||
    startingConfiguration ||
    configurationIsRunning;

  function renderStatus() {
    if (loading) {
      return (
        <div>
          <p>Configuration in progress...</p>
        </div>
      );
    }

    if (startConfigurationError || previousJob?.status === JobStatus.FAILED) {
      const errorMessage = startConfigurationError || previousJob?.errorMessage;
      return (
        <div>
          <p>
            Error configuring messaging number
            {errorMessage ? `: ${errorMessage}` : "."}
          </p>
        </div>
      );
    }

    // Messaging number is set
    if (subaccount.messagingPhoneNumber) {
      // Messaging number is active
      if (
        messagingNumberIsActive(
          checkStatusFlag,
          subaccount.messagingPhoneNumberStatus
        )
      ) {
        return (
          <>
            <ActiveMessagingNumberMessage
              number={subaccount.messagingPhoneNumber}
            />
            <div>
              <p>
                Current phone number status:{" "}
                {subaccount.messagingPhoneNumberStatus}.
              </p>
            </div>
          </>
        );
      } else {
        // Messaging number inactive/unregistered
        return (
          <div>
            <p>
              Awaiting registration of number{" "}
              <strong>
                {formatPhoneNumber(subaccount.messagingPhoneNumber)}
              </strong>
              . Current status:{" "}
              <strong>{subaccount.messagingPhoneNumberStatus}</strong>.
            </p>
          </div>
        );
      }
    }
  }

  return (
    <Card>
      <CardHeader title="Messaging" />
      <CardContent>
        {renderStatus()}
        <StaffNumberControls
          handleConfigureMessaging={handleConfigureMessaging}
          disabled={loading}
        />
      </CardContent>
    </Card>
  );
}

function ActiveMessagingNumberMessage({
  number
}: {
  number: string;
}): JSX.Element {
  const tenant = useTenant()!;
  return (
    <div>
      <p>
        Text messaging is enabled for this{" "}
        {tenant.isUsingSubaccounts ? "subaccount" : "organization"}.
      </p>
      <p>
        Messages will be sent from <strong>{formatPhoneNumber(number)}</strong>.
      </p>
    </div>
  );
}

type Values = {
  messagingPhoneNumber: string;
  messagingPhoneNumberStatus: string;
};

function StaffNumberControls({
  handleConfigureMessaging,
  disabled
}: {
  handleConfigureMessaging: () => Promise<void>;
  disabled?: boolean;
}): JSX.Element {
  const { subaccount } = useSubaccount()!;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [formType, setFormType] = useState<"UpdateStatus" | "Import">();
  const [open, setOpenDialog] = useState(false);
  const tenant = useTenant()!;

  const [editMessagingNumber] = useMutation(EDIT_MESSAGING_NUMBER, {
    refetchQueries: ["loggedInInfo"],
    awaitRefetchQueries: true
  });

  const showUpdateStatusDialog = () => {
    setFormType("UpdateStatus");
    setOpenDialog(true);
  };

  const showImportDialog = () => {
    setFormType("Import");
    setOpenDialog(true);
  };

  const handleSave = async (values: Values) => {
    const { errors } = await editMessagingNumber({
      variables: {
        subaccountId: subaccount.id,
        ...values
      }
    });
    if (errors) {
      enqueueSnackbar(errors[0].message, { variant: "error" });
    } else {
      setOpenDialog(false);
    }
  };

  const editMessageSchema = yup.object().shape({
    messagingPhoneNumber: yup
      .string()
      .phoneNumber(tenant.countryIso)
      .required("Required"),
    messagingPhoneNumberStatus: yup.string().required("Required")
  });

  const formTitle =
    formType === "UpdateStatus" ? "Update status" : "Import number";
  const initialPhoneNumber =
    formType === "UpdateStatus" ? subaccount.messagingPhoneNumber || "" : "";
  const action = formType === "UpdateStatus" ? "Update" : "Import";
  const actionWhileSubmiting =
    formType === "UpdateStatus" ? "Updating..." : "Importing...";

  return (
    <>
      <Dialog open={open} aria-labelledby="alert-dialog-title" fullWidth>
        <Formik
          initialValues={{
            messagingPhoneNumber: initialPhoneNumber,
            messagingPhoneNumberStatus:
              subaccount.messagingPhoneNumberStatus || ""
          }}
          onSubmit={handleSave}
          validationSchema={editMessageSchema}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <DialogTitle id="alert-dialog-title">{formTitle}</DialogTitle>
              <DialogContent>
                <FormikErrorBanner />
                {formType === "Import" ? (
                  <FormikTextField
                    name="messagingPhoneNumber"
                    label="Number in Bandwidth to import"
                    fullWidth
                  />
                ) : null}
                <FormikSelect
                  label="Status of messaging phone number"
                  name="messagingPhoneNumberStatus"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {[
                    MessagingPhoneNumberStatus.unregistered,
                    MessagingPhoneNumberStatus.active,
                    MessagingPhoneNumberStatus.tollfree
                  ].map(status => (
                    <MenuItem key={`status-${status}`} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </DialogContent>
              <DialogActions>
                <Button
                  disabled={isSubmitting}
                  onClick={() => setOpenDialog(false)}
                  color="primary"
                >
                  Cancel
                </Button>
                <SubmitButton
                  type="submit"
                  disabled={!dirty}
                  isSubmitting={isSubmitting}
                  submitText={action}
                  loadingText={actionWhileSubmiting}
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      <div className={classes.buttonContainer}>
        {subaccount.messagingPhoneNumberStatus ===
        MessagingPhoneNumberStatus.unregistered ? (
          <Button
            variant="contained"
            color="primary"
            onClick={showUpdateStatusDialog}
            disabled={disabled}
          >
            Update status
          </Button>
        ) : null}

        <Button
          variant="contained"
          color="primary"
          onClick={handleConfigureMessaging}
          disabled={disabled}
        >
          Provision number with {subaccount.settings.campaignDefaultAreaCode}{" "}
          area code
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={showImportDialog}
          disabled={disabled}
        >
          Import number
        </Button>
      </div>
    </>
  );
}
